<!-- ═══════════════════════════════════════════════════════════════════════════
     Section: LIVE STREAMS — active binary markets
     id="activity" anchored for header nav
  ════════════════════════════════════════════════════════════════════════════ -->
<section class="streams" id="activity" aria-labelledby="streams-heading">
  <div class="streams__container">

    <!-- Section header -->
    <div class="streams__header">
      <div class="streams__header-left">
        <h2 class="streams__title" id="streams-heading">
          {{ 'how-to-buy.streams_title' | translate }}
        </h2>
        <div class="streams__title-underline" aria-hidden="true"></div>
      </div>

      <!-- Navigation arrows (functional on mobile, decorative on desktop) -->
      <div class="streams__arrows" aria-label="Scroll markets">
        <button class="streams__arrow" (click)="scrollLeft()" aria-label="Previous markets">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="white" stroke-width="1.5" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <button class="streams__arrow" (click)="scrollRight()" aria-label="Next markets">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Market cards — scrollable on mobile -->
    <div class="streams__grid" #slider>
      @for (card of markets; track card.assetKey; let i = $index) {
      <article class="streams__card" [style.--badge-color]="card.badgeColor">
        <span class="shadow shadow-{{i+1}}"></span>
        <!-- Asset badge -->
        <div class="streams__asset-badge">
          {{ card.assetKey | translate }}
        </div>

        <!-- Market question -->
        <h3 class="streams__question mb-3">{{ card.questionKey | translate }}</h3>

        <!-- Decorative glow blob -->
        <div class="streams__glow" aria-hidden="true"></div>

        <!-- YES / NO buttons -->
        <div class="streams__actions">
          <button class="streams__vote streams__vote--yes">
            <strong>{{ 'how-to-buy.yes_label' | translate }} {{ card.yesMultiplier }}</strong>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 10L10 2M10 2H4M10 2V8" stroke="#C3F400" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
          <button class="streams__vote streams__vote--no">
            <strong>{{ 'how-to-buy.no_label' | translate }} {{ card.noMultiplier }}</strong>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 2L10 10M10 10H4M10 10V4" stroke="#FF51FA" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>

      </article>
      }
    </div>

  </div>
</section>
<!-- ═══════════════════════════════════════════════════════════════════════════
     Section: THE DAO COUNCIL — governance
     id="docs" anchored for header nav
  ════════════════════════════════════════════════════════════════════════════ -->
<section class="dao" id="dao" aria-labelledby="dao-heading">

  <!-- Background glow blobs (CSS) -->
  <div class="dao__blob dao__blob--lime" aria-hidden="true"></div>
  <div class="dao__blob dao__blob--cyan" aria-hidden="true"></div>

  <div class="dao__container">
    <div class="dao__grid">

      <!-- ─── Left: Text + Proposal Card ──────────────────────────────────── -->
      <div class="dao__content">

        <p class="dao__eyebrow">{{ 'how-to-buy.dao_eyebrow' | translate }}</p>

        <h2 class="dao__title" id="dao-heading">
          <span>{{ 'how-to-buy.dao_title_1' | translate }}</span>
          <span class="dao__title--accent">{{ 'how-to-buy.dao_title_2' | translate }}</span>
        </h2>

        <p class="dao__body my-4">{{ 'how-to-buy.dao_body' | translate }}</p>

        <!-- Active Proposal card -->
        <div class="dao__proposal" role="region" aria-label="Active governance proposal">

          <div class="dao__proposal-header">
            <span class="dao__proposal-label">{{ 'how-to-buy.dao_proposal_label' | translate }}</span>
            <span class="dao__proposal-ends">{{ 'how-to-buy.dao_voting_ends' | translate }}</span>
          </div>

          <p class="dao__proposal-title">{{ 'how-to-buy.dao_proposal_title' | translate }}</p>

          <div class="d-flex align-items-start gap-3">
            <!-- FOR progress -->
            <div class="dao__vote-row w-100">
              <div class="dao__vote-meta">
                <span class="dao__vote-label dao__vote-label--for">{{ 'how-to-buy.dao_for_label' | translate }}</span>
                <span class="dao__vote-amount">{{ 'how-to-buy.dao_for_amount' | translate }}</span>
              </div>
              <div class="dao__progress-track" role="progressbar" aria-valuenow="72" aria-valuemin="0"
                aria-valuemax="100">
                <div class="dao__progress-fill dao__progress-fill--for" style="width: 72%"></div>
              </div>
            </div>

            <!-- AGAINST progress -->
            <div class="dao__vote-row w-100">
              <div class="dao__vote-meta">
                <span class="dao__vote-label dao__vote-label--against">{{ 'how-to-buy.dao_against_label' | translate
                  }}</span>
                <span class="dao__vote-amount">{{ 'how-to-buy.dao_against_amount' | translate }}</span>
              </div>
              <div class="dao__progress-track" role="progressbar" aria-valuenow="28" aria-valuemin="0"
                aria-valuemax="100">
                <div class="dao__progress-fill dao__progress-fill--against" style="width: 28%"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── Right: HUD Governance Visualization ──────────────────────────── -->
      <div class="dao__hud" aria-label="Governance protocol visualization">
        <div class="dao__hud-card">
          <div class="dao__hud-wrapper h-100 w-100">

            <div class="dao__hud-outer">
              <div class="dao__hud-middle ">


                <div class="dao__hud-inner position-relative">
                  <span class="dao__hud-sig">{{ 'how-to-buy.dao_hud_sig' | translate }}</span>
                  <span class="dao__hud-version">{{ 'how-to-buy.dao_hud_version' | translate }}</span>
                  <!-- Center stats -->
                  <div class="dao__hud-center">
                    <span class="dao__hud-control">{{ 'how-to-buy.dao_hud_control' | translate }}</span>
                    <strong class="dao__hud-percent">{{ 'how-to-buy.dao_hud_percent' | translate }}</strong>
                    <span class="dao__hud-quorum">{{ 'how-to-buy.dao_hud_quorum' | translate }}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>
          <!-- Nested concentric border frames -->

          <!-- HUD data labels -->
          <!-- <div class="dao__hud-labels">
          </div> -->

        </div>
      </div>

    </div>
  </div>
</section>
